// WorkRowActions — the per-row "⋯" menu on every Work list.
//
// Fixing work that went to the wrong person used to mean opening the item and
// finding an 11px "Reassign" link in the side panel, or a "Cancel item" button
// at the very bottom. The list is where people notice the mistake, so the
// corrections live on the row.
//
// WHICH actions show is the server's decision, per item: opening the menu
// fetches the item's `viewer` flags (the same rule each action enforces —
// owner / creator / manager of the owner / work admin). A list page would
// otherwise have to guess per row, and a guess is either a button the server
// refuses or a missing one it would have allowed. The server still re-checks
// every action, and its refusal is shown verbatim.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowRightLeft, Ban, ExternalLink, Loader2, MoreHorizontal, Repeat, Trash2, Undo2, UserMinus } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/features/shared/components/ui/dropdown-menu';
import { useConfirmIfAvailable } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { WorkItem, WorkItemDetail, cancelWork, getWorkItem, stopWorkSeries } from '@/services/workService';
import { CancelWorkDialog } from './CancelWorkDialog';
import { ReassignDialog, handBackLockedAfterStart } from './shared';
import { UnassignWorkDialog } from './UnassignWorkDialog';
import { RemoveWorkDialog, restoreWithFeedback } from './RemoveWorkDialog';
import { useEligibleAssignees, useMyEmployeeId } from '@/features/work/api/hooks';
import { useHasPermission } from '@/rbac/useRbac';

export const WorkRowActions: React.FC<{
  item: WorkItem;
  ownerName?: string;
  onOpen: (id: string) => void;
  /** Called after any successful correction so the list refetches. */
  onChanged: () => void;
}> = ({ item, ownerName, onOpen, onChanged }) => {
  const { t } = useTranslation('work');
  const confirm = useConfirmIfAvailable();
  const [detail, setDetail] = useState<WorkItemDetail | null>(null);
  const [loading, setLoading] = useState(false);
  // Why the permission check failed — the server's words (W29), never a bare
  // "could not check" that leaves people guessing.
  const [failed, setFailed] = useState<string | null>(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const myEmployeeId = useMyEmployeeId();
  // Restore is a work:remove decision; the server re-checks it on every call.
  const canRemoveWork = useHasPermission('work:remove');
  const canWriteWork = useHasPermission('work:write');
  const [busy, setBusy] = useState(false);
  const fetchAssignees = useEligibleAssignees('assign', { workItemId: item.id });

  // Ask the server what THIS caller may do with THIS item, each time the menu
  // opens (the answer changes as the item moves).
  const onOpenChange = (open: boolean) => {
    if (!open) return;
    setLoading(true);
    setFailed(null);
    // A removed row (the Removed view) is read with include_removed — only
    // administrators see that view, and only they get Restore.
    (item.removed_at ? getWorkItem(item.id, { includeRemoved: true }) : getWorkItem(item.id))
      .then(setDetail)
      .catch((e) => { setDetail(null); setFailed(getApiErrorMessage(e, t('row_check_failed_reason', { defaultValue: 'Open the item instead.' }))); })
      .finally(() => setLoading(false));
  };

  const viewer = detail?.viewer;
  const current = detail?.work_item ?? item;
  const openSubitems = detail?.subitems?.open_descendants ?? 0;
  const canReassign = !!viewer?.can_reassign;
  const canCancel = !!viewer?.can_cancel;
  const canUnassign = !!viewer?.can_unassign;
  const canRemove = !!viewer?.can_remove;
  // W29: when the check fails on a removed row, an administrator still gets
  // Restore — it does not depend on the read that failed, and the server
  // decides. Without it the row was a dead end (Open + an error, nothing else).
  const canRestore = !!viewer?.can_restore || (!!failed && !!item.removed_at && canRemoveWork);
  // The owner removing themselves is a hand-back, and is labelled as one.
  const isOwner = !!viewer?.is_owner || (!!myEmployeeId && current.owner_employee_id === myEmployeeId);
  const canStopSeries = !!viewer?.can_stop_series && !!viewer?.series_id && !!confirm;
  // W14: once the owner has started, Hand back stays in the menu, disabled,
  // with the reason — it used to just vanish.
  const handBackLocked = !!detail && handBackLockedAfterStart({
    isOwner, canUnassign: viewer?.can_unassign, status: current.status, canWrite: canWriteWork,
  });
  const nothing = !!detail && !canReassign && !canUnassign && !handBackLocked && !canCancel && !canStopSeries && !canRemove && !canRestore;

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onChanged();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('action_failed', { defaultValue: 'Action failed.' })));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    const r = await restoreWithFeedback(item.id, t);
    setBusy(false);
    if (r) onChanged();
  };

  const stopSeries = async () => {
    if (!viewer?.series_id || !confirm) return;
    const ok = await confirm({
      title: t('stop_series_title', { defaultValue: 'Stop this repeating work?' }) as string,
      message: t('stop_series_body', { defaultValue: 'No new occurrences will be created. Work already created stays as it is — cancel or reassign those one by one.' }) as string,
      confirmLabel: t('stop_series', { defaultValue: 'Stop repeating' }) as string,
      cancelLabel: t('keep', { defaultValue: 'Keep it' }) as string,
      danger: true,
    });
    if (!ok) return;
    await run(() => stopWorkSeries(viewer.series_id as string), t('stop_series_ok', { defaultValue: 'This work will no longer repeat.' }) as string);
  };

  return (
    <>
      <DropdownMenu onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <button type="button" disabled={busy}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('row_actions_aria', { defaultValue: 'Actions for {{title}}', title: item.title }) as string}
            data-testid={`work-row-actions-${item.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onSelect={() => onOpen(item.id)}>
            <ExternalLink className="mr-2 h-4 w-4" />{t('row_open', { defaultValue: 'Open' })}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {loading && (
            <DropdownMenuLabel className="flex items-center gap-2 font-normal text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />{t('row_checking', { defaultValue: 'Checking what you can change…' })}
            </DropdownMenuLabel>
          )}
          {/* A failed check is NOT "nothing to change" — say it failed. */}
          {!loading && failed && (
            <DropdownMenuLabel className="font-normal text-danger-strong">
              {t('row_check_failed_with', { defaultValue: 'Could not check what you can change: {{reason}}', reason: failed })}
            </DropdownMenuLabel>
          )}
          {!loading && canReassign && (
            <DropdownMenuItem onSelect={() => setReassignOpen(true)}>
              <ArrowRightLeft className="mr-2 h-4 w-4" />{t('reassign', { defaultValue: 'Reassign' })}
            </DropdownMenuItem>
          )}
          {!loading && canUnassign && (
            <DropdownMenuItem onSelect={() => setUnassignOpen(true)}>
              <UserMinus className="mr-2 h-4 w-4" />
              {isOwner ? t('hand_back', { defaultValue: 'Hand back' }) : t('unassign', { defaultValue: 'Remove assignment' })}
            </DropdownMenuItem>
          )}
          {!loading && handBackLocked && (
            <>
              <DropdownMenuItem disabled aria-describedby={`hand-back-locked-row-${item.id}`} data-testid="hand-back-locked">
                <UserMinus className="mr-2 h-4 w-4" />{t('hand_back', { defaultValue: 'Hand back' })}
              </DropdownMenuItem>
              <DropdownMenuLabel id={`hand-back-locked-row-${item.id}`} className="pt-0 text-xs font-normal text-muted-foreground">
                {t('hand_back_locked_hint', { defaultValue: 'Hand back is only available before you start the item. Ask your manager to reassign it.' })}
              </DropdownMenuLabel>
            </>
          )}
          {!loading && canCancel && (
            <DropdownMenuItem onSelect={() => setCancelOpen(true)} className="text-danger-strong focus:text-danger-strong">
              <Ban className="mr-2 h-4 w-4" />{t('cancel_item', { defaultValue: 'Cancel item' })}
            </DropdownMenuItem>
          )}
          {!loading && canStopSeries && (
            <DropdownMenuItem onSelect={() => void stopSeries()}>
              <Repeat className="mr-2 h-4 w-4" />{t('stop_series', { defaultValue: 'Stop repeating' })}
            </DropdownMenuItem>
          )}
          {!loading && canRemove && (
            <DropdownMenuItem onSelect={() => setRemoveOpen(true)} className="text-danger-strong focus:text-danger-strong">
              <Trash2 className="mr-2 h-4 w-4" />{t('remove', { defaultValue: 'Remove' })}
            </DropdownMenuItem>
          )}
          {!loading && canRestore && (
            <DropdownMenuItem onSelect={() => void restore()}>
              <Undo2 className="mr-2 h-4 w-4" />{t('restore', { defaultValue: 'Restore' })}
            </DropdownMenuItem>
          )}
          {!loading && nothing && (
            <DropdownMenuLabel className="font-normal text-muted-foreground">
              {t('row_no_actions', { defaultValue: 'Only its owner, its creator or their manager can change this item.' })}
            </DropdownMenuLabel>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ReassignDialog open={reassignOpen} workItemId={item.id}
        currentOwnerId={current.owner_employee_id}
        currentOwnerName={current.owner_employee_id ? (current.owner_name || ownerName) : undefined}
        hasOpenSubitems={openSubitems > 0} expectedUpdatedAt={current.updated_at}
        subtask={!!current.parent_id}
        fetchPage={fetchAssignees}
        onClose={() => setReassignOpen(false)}
        onDone={() => onChanged()} />
      <UnassignWorkDialog open={unassignOpen} workItemId={item.id}
        ownerName={current.owner_employee_id ? (current.owner_name || ownerName) : undefined}
        selfHandBack={isOwner} hasOpenSubitems={openSubitems > 0} expectedUpdatedAt={current.updated_at}
        onClose={() => setUnassignOpen(false)}
        onDone={() => onChanged()} />
      <RemoveWorkDialog open={removeOpen} item={current} subitems={detail?.subitems?.total ?? 0}
        onClose={() => setRemoveOpen(false)}
        onDone={() => onChanged()} />
      <CancelWorkDialog open={cancelOpen} busy={busy} openSubitems={openSubitems}
        onClose={() => setCancelOpen(false)}
        onConfirm={(opts) => {
          setCancelOpen(false);
          void run(() => cancelWork(item.id, { ...opts, expectedUpdatedAt: current.updated_at }),
            t('cancelled_ok', { defaultValue: 'Work item cancelled.' }) as string);
        }} />
    </>
  );
};

export default WorkRowActions;
